/* # UserRoles Modification
1. Homogenized UI to match Branding section's crisp look
2. Updated typography (font weights, sizes)
3. Refined card and modal styling
*/
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../../services/api';

const { FiPlus, FiEdit3, FiTrash2, FiShield, FiCheck, FiX, FiLock } = FiIcons;

const UserRoles = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', permissions: [] });

    const availablePermissions = [
        { id: 'manage_users', label: 'Manage Users', description: 'Create, edit and delete user accounts (Lead can only manage own team)' },
        { id: 'manage_billing', label: 'Manage Billing', description: 'Manage subscriptions and invoices' },
        { id: 'system_settings', label: 'System Settings', description: 'Access and modify application configuration' },
        { id: 'view_reports', label: 'View Reports', description: 'Access analytics and usage reports' },
        { id: 'branding_settings', label: 'Branding Settings', description: 'Modify logo, colors and theme' },
        { id: 'view_own_data', label: 'View Own Data', description: 'Base permission for all users' },
        { id: 'expense_tracking', label: 'Expense Tracking', description: 'Access core expense tracking features' }
    ];

    useEffect(() => { loadRoles(); }, []);

    const loadRoles = async () => {
        try {
            const data = await api.get('roles');
            setRoles(data.data.data);
        } catch (error) {
            toast.error('Failed to load roles');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (role = null) => {
        if (role) {
            setEditingRole(role);
            setFormData({ id: role.id, name: role.name, description: role.description, permissions: role.permissions || [] });
        } else {
            setEditingRole(null);
            setFormData({ name: '', description: '', permissions: [] });
        }
        setIsModalOpen(true);
    };

    const handleSaveRole = async () => {
        if (!formData.name) return toast.error('Role name is required');

        try {
            if (formData.id) {
                // Edit existing role
                await api.put(`roles/${formData.id}`, formData);
                toast.success('Role updated');
            } else {
                // Create new role
                await api.post('roles', formData);
                toast.success('New role created');
            }

            await loadRoles();           // Refresh roles list
            setIsModalOpen(false);       // Close modal
        } catch (error) {
            console.error(error);
            toast.error('Failed to save role');
        }
    };

    const togglePermission = (permId) => {
        const perm = availablePermissions.find(p => p.id === permId);
        if (!perm) return;

        setFormData(prev => {
            const hasPerm = prev.permissions.includes(perm.label);

            if (hasPerm) {
                // Remove permission
                return {
                    ...prev,
                    permissions: prev.permissions.filter(p => p !== perm.label)
                };
            } else {
                // Add permission label
                return {
                    ...prev,
                    permissions: [...prev.permissions, perm.label]
                };
            }
        });
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Roles & Permissions</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">Configure system access levels and permissions</p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center justify-center px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all shadow-md font-medium"
                    >
                        <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
                        Add New Role
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {roles.map((role) => (
                        <div key={role.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                                    <SafeIcon icon={FiShield} className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                                </div>
                                {role.isEditable !== false ? (
                                    <div className="flex space-x-2">
                                        <button onClick={() => handleOpenModal(role)} className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"><SafeIcon icon={FiEdit3} className="w-5 h-5" /></button>
                                        <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"><SafeIcon icon={FiTrash2} className="w-5 h-5" /></button>
                                    </div>
                                ) : (
                                    <div className="p-1.5 bg-gray-50 dark:bg-gray-700 rounded">
                                        <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-400" />
                                    </div>
                                )}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{role.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2 min-h-[2.5rem]">{role.description}</p>
                            <div className="flex flex-wrap gap-2">
                                {role.permissions?.map(p => (
                                    <span key={p} className="px-2.5 py-1 bg-gray-50 dark:bg-gray-700 text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider rounded-md border border-gray-100 dark:border-gray-600">
                                        {p.replace(/_/g, ' ')}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
                                <button onClick={() => setIsModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"><SafeIcon icon={FiX} className="w-6 h-6" /></button>
                            </div>
                            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
                                <div className="grid grid-cols-1 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Role Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Content Manager"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                                        <textarea
                                            placeholder="Briefly describe what this role can do"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all min-h-[80px]"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Permissions</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {availablePermissions.map(perm => {
                                            const hasPermission = formData.permissions.includes(perm.label);

                                            return (
                                                <button
                                                    key={perm.id}
                                                    onClick={() => togglePermission(perm.id)}
                                                    className={`flex items-start p-3 rounded-xl border transition-all text-left ${hasPermission
                                                        ? 'bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800 ring-1 ring-primary-500/20'
                                                        : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                    }`}
                                                >
                                                    <div className={`w-5 h-5 mt-0.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${hasPermission
                                                        ? 'bg-primary-600 border-primary-600'
                                                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                                    }`}>
                                                        {hasPermission && <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-white" />}
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{perm.label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{perm.description}</p>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-end space-x-3">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveRole}
                                    className="px-6 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all shadow-md active:scale-95"
                                >
                                    Save Role
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default UserRoles;